import { DataProvider, fetchUtils } from 'react-admin';
import { tenantService } from '../services/tenantService';

const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL || 'http://localhost:3333';

/**
 * Franchise-specific resources
 * These map to the franchise API endpoints in distill-hq-server
 */
export const franchiseResources = [
  'enterprises',
  'brands',
  'regions',
  'tenants',
  'brand-service-templates',
  'brand-workflows',
  'franchise-analytics/enterprise',
  'franchise-analytics/brand',
  'franchise-analytics/region',
  'franchise-analytics/tenant',
];

/**
 * HTTP client with authentication
 * Note: Franchise endpoints operate at the enterprise level and don't need tenant context
 */
const httpClient = async (url: string, options: any = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }

  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    options.headers.set('Authorization', `Bearer ${accessToken}`);
  }

  // Franchise endpoints DO NOT use tenant context
  // They work at the enterprise level for enterprise admins
  // No need to set x-tenant-code header

  return fetchUtils.fetchJson(url, options);
};

/**
 * Franchise Data Provider
 *
 * Handles API calls for franchise management endpoints:
 * - Enterprises
 * - Brands
 * - Regions
 * - Analytics
 */
export const franchiseDataProvider: DataProvider = {
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const query = {
      sort: field,
      order: order.toLowerCase(),
      page: page.toString(),
      perPage: perPage.toString(),
      ...params.filter,
    };

    const url = `${baseUrl}/${resource}?${new URLSearchParams(query).toString()}`;

    try {
      const { json } = await httpClient(url);

      // Handle different response formats
      let data = [];
      let total = 0;

      if (Array.isArray(json)) {
        data = json;
        total = json.length;
      } else if (json.data && Array.isArray(json.data)) {
        data = json.data;
        total = json.metadata?.total || json.total || data.length;
      } else {
        data = [json];
        total = 1;
      }

      // For tenants resource, map 'code' to 'id' since Tenant uses code as primary identifier
      if (resource === 'tenants') {
        data = data.map((item) => ({
          ...item,
          id: item.code,
        }));
      }

      return {
        data,
        total,
      };
    } catch (error) {
      console.error(`Error fetching ${resource}:`, error);
      throw error;
    }
  },

  getOne: async (resource, params) => {
    const url = `${baseUrl}/${resource}/${params.id}`;

    try {
      const { json } = await httpClient(url);
      // Analytics endpoints don't return an id field, so add it to satisfy React Admin
      // If the response doesn't have an id, use the params.id as fallback
      return {
        data: {
          ...json,
          id: json.id || params.id
        }
      };
    } catch (error) {
      console.error(`Error fetching ${resource} ${params.id}:`, error);
      throw error;
    }
  },

  getMany: async (resource, params) => {
    // For getMany, we'll make individual requests
    // This could be optimized with a batch endpoint if needed
    try {
      const promises = params.ids.map((id) =>
        httpClient(`${baseUrl}/${resource}/${id}`).then(({ json }) => json)
      );
      const data = await Promise.all(promises);
      return { data };
    } catch (error) {
      console.error(`Error fetching multiple ${resource}:`, error);
      throw error;
    }
  },

  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const query = {
      sort: field,
      order: order.toLowerCase(),
      page: page.toString(),
      perPage: perPage.toString(),
      [params.target]: params.id,
      ...params.filter,
    };

    const url = `${baseUrl}/${resource}?${new URLSearchParams(query).toString()}`;

    try {
      const { json } = await httpClient(url);

      let data = [];
      let total = 0;

      if (Array.isArray(json)) {
        data = json;
        total = json.length;
      } else if (json.data && Array.isArray(json.data)) {
        data = json.data;
        total = json.metadata?.total || json.total || data.length;
      }

      // For tenants resource, map 'code' to 'id' since Tenant uses code as primary identifier
      if (resource === 'tenants') {
        data = data.map((item) => ({
          ...item,
          id: item.code,
        }));
      }

      return {
        data,
        total,
      };
    } catch (error) {
      console.error(`Error fetching ${resource} reference:`, error);
      throw error;
    }
  },

  create: async (resource, params) => {
    const url = `${baseUrl}/${resource}`;

    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(params.data),
      });
      return { data: { ...params.data, id: json.id } };
    } catch (error) {
      console.error(`Error creating ${resource}:`, error);
      throw error;
    }
  },

  update: async (resource, params) => {
    const url = `${baseUrl}/${resource}/${params.id}`;

    try {
      const { json } = await httpClient(url, {
        method: 'PATCH',
        body: JSON.stringify(params.data),
      });
      return { data: json };
    } catch (error) {
      console.error(`Error updating ${resource} ${params.id}:`, error);
      throw error;
    }
  },

  updateMany: async (resource, params) => {
    try {
      const promises = params.ids.map((id) =>
        httpClient(`${baseUrl}/${resource}/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(params.data),
        })
      );
      await Promise.all(promises);
      return { data: params.ids };
    } catch (error) {
      console.error(`Error updating multiple ${resource}:`, error);
      throw error;
    }
  },

  delete: async (resource, params) => {
    const url = `${baseUrl}/${resource}/${params.id}`;

    try {
      const { json } = await httpClient(url, {
        method: 'DELETE',
      });
      return { data: json };
    } catch (error) {
      console.error(`Error deleting ${resource} ${params.id}:`, error);
      throw error;
    }
  },

  deleteMany: async (resource, params) => {
    try {
      const promises = params.ids.map((id) =>
        httpClient(`${baseUrl}/${resource}/${id}`, {
          method: 'DELETE',
        })
      );
      await Promise.all(promises);
      return { data: params.ids };
    } catch (error) {
      console.error(`Error deleting multiple ${resource}:`, error);
      throw error;
    }
  },
};
